import {
  Accordion,
  ActionIcon,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Radio,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { randomId, useDebouncedValue, useFocusTrap } from '@mantine/hooks';
import { closeAllModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { ErrorBoundary } from '@sentry/react';
import { IconFileDescription, IconInfoCircle, IconLink, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DatasetsFilesInfo,
  FAQDetail,
  FAQsBase,
  FAQsItem,
  getDatasetsFilesList,
  safeDecodeURI,
  useFAQsQuestionsMethods,
  useNotIdFAQsMethods,
} from '@mygpt/app';
import { useFilePreviewStore } from '@mygpt/app/chat/stores/useFilePreviewStore';
import { PDFViewer } from '@mygpt/app/chat/components/PDFViewer';
import { FAQsPropertyInfo, MsgCitation } from '@mygpt/types';
import { ErrorContent } from '@/components/ErrorContent';
import { FaqsDatasetForm } from '@/components/Faqs/form-dataset';
import { RichEditor } from '@/components/TiptapRichEditor/compat';
import { useDatasetStore } from '@/store/dataset';

import classes from './item-edit.module.css';

export interface FaqsItemEditProps {
  datasetId: string;
  selectDatasetId?: boolean; // 可以选择数据集
  faqs?: Partial<FAQsItem>;
  faqId?: string | null; // 是否需要根据ID重新查询FAQ信息(历史会话)
  citation?: MsgCitation; // 来源

  onSubmit?: (faqs: FAQsBase) => Promise<void>;
  onSubmitText?: string;
  footerComponent?: React.FC<{ loading: boolean; disabled: boolean }>;
}

interface Question {
  question: string;
  key: string;
}

interface FormValues {
  question: string;
  similar_questions: Question[];
  recommended_questions: Question[];
  answer: string;
  sources: string;
  rating: number;
  property_info: FAQsPropertyInfo[];
  datasetId: string;
  sourceSearch?: string;
  type?: 'answer' | 'source';
  page_numbers?: string;
  selectedFile?: DatasetsFilesInfo;
  is_search?: boolean;
  sync_translations?: boolean;
}

export const FaqsItemEdit: FC<FaqsItemEditProps> = ({
  citation,
  datasetId,
  faqId,
  faqs,
  footerComponent: FooterComponent,
  onSubmit,
  onSubmitText,
  selectDatasetId,
}) => {
  const { t, i18n } = useTranslation();

  const [inited, setInited] = useState(true);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  // const [sourceCitation, setSourceCitation] = useState<MsgCitation>(citation || []);
  const [answerType, setAnswerType] = useState('answer');
  const [answerTypeLoading, setAnswerTypeLoading] = useState<boolean>(!!faqId);

  const [sourceSearch, setSourceSearch] = useState('');

  const [debouncedSearch] = useDebouncedValue(sourceSearch, 500);
  const [searchFileLoading, setSearchFileLoading] = useState<boolean>(false);
  const [filesList, setFilesList] = useState<DatasetsFilesInfo[]>([]);
  const [selectedFile, setSelectedFile] = useState<DatasetsFilesInfo | null>(null);
  const [filePageCount, setFilePageCount] = useState('');

  const { isCloning, setCloningTipOpened } = useDatasetStore();
  const focusTrapRef = useFocusTrap();
  const { detailFAQ } = useNotIdFAQsMethods();
  const { generateSimilarQuestions, loading: generateLoading } = useFAQsQuestionsMethods();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  const form = useForm<FormValues>({
    initialValues: {
      question: '',
      similar_questions: [], // 相似问题
      recommended_questions: [], // 推荐问题
      sourceSearch: '',
      answer: '',
      sources: '',
      rating: 1,
      property_info: [],
      datasetId: '',
      is_search: true,
      sync_translations: true,
    },
    transformValues: (values: FormValues) => ({
      ...values,
      question: values.question.trim(),
      similar_questions: values.similar_questions?.filter((item) => !!item.question.trim()),
      recommended_questions: values.recommended_questions?.filter((item) => !!item.question.trim()),
    }),
    validate: (values) => {
      const errors: Partial<Record<keyof typeof values, string | null | boolean>> = {};
      if (!values.question) {
        errors['question'] = t('fields.question_invalid');
      }
      if (!values.type) {
        errors['type'] = t('faqs.submit_tip.choose_type');
      }
      if (values.type === 'source') {
        if (values.sourceSearch && !values.selectedFile) {
          errors['sourceSearch'] = t('faqs.submit_tip.choose_type');
        }
        if (!values.sourceSearch && !values.selectedFile) {
          errors['sourceSearch'] = t('faqs.submit_tip.source_search');
        }
        if (values.selectedFile && values.selectedFile.file_type === 'upload' && !values.page_numbers) {
          errors['page_numbers'] = true;
        }
      } else {
        if (!values.answer) {
          errors['answer'] = t('fields.answer_invalid');
        }
      }
      return errors;
    },
  });

  const DatasetId = useMemo(() => {
    return selectDatasetId ? form.values.datasetId : datasetId;
  }, [form.values.datasetId, datasetId, selectDatasetId]);

  const HasQuestionValue = useMemo(() => {
    return !!form.values.question.trim();
  }, [form.values.question]);

  const richEditorOnChange = useCallback(
    (value: string) => {
      form.setFieldValue('answer', value);
    },
    [form.setFieldValue]
  );

  // const handleLinksChange = (links: { val: string; title: string }[]) => {
  //   const translateLinks = links.map((item) => {
  //     return {
  //       ...item,
  //       type: 'url',
  //     };
  //   });
  //   setSourceCitation(translateLinks);
  // };

  const generateSQ = async () => {
    if (!DatasetId) return;
    const data = await generateSimilarQuestions({ datasetId: DatasetId, question: form.values.question, count: 5 });
    const similarQuestions = data.map((item) => ({
      question: item,
      key: randomId(),
    }));
    form.setFieldValue('similar_questions', similarQuestions);
  };

  const addCustomFormQuestions = (key: 'similar_questions' | 'recommended_questions') => {
    if (form.values[key].length >= 10) {
      showNotification({
        message: t('errors.data_limit'),
        color: 'red',
      });
      return;
    }
    form.insertListItem(key, { question: '', key: randomId() });
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    setLoading(true);

    // const citation = sourceCitation.map((item) => {
    //   return {
    //     ...item,
    //     val: autoCompleteUrlScheme(item.val),
    //   };
    // });

    delete values.sourceSearch;
    delete values.selectedFile;
    delete values.page_numbers;
    // 「同步到其它语言」只在编辑既有 Q&A 时生效，新建走的是另一条后端接口
    if (!faqId) delete values.sync_translations;

    onSubmit?.(
      Object.assign(
        values,
        {
          // citation,
          similar_questions: values.similar_questions?.map((item) => item.question),
          recommended_questions: values.recommended_questions?.map((item) => item.question),
          type: answerType,
          is_search: values.is_search,
          language: i18n.language,
        },
        answerType === 'source' && {
          resources: [
            {
              vector_file_id: selectedFile?.id,
              page_numbers: filePageCount ? filePageCount.split(',').map((p) => parseInt(p)) : [],
            },
          ],
        }
      )
    ).finally(() => {
      setLoading(false);
    });
  };

  const formInit = async () => {
    const values: FormValues = {
      datasetId: DatasetId,
      question: faqs?.question ?? '',
      answer: faqs?.answer ?? '',
      sources: faqs?.sources ?? '',
      type: faqs?.type ?? 'answer',
      rating: faqs?.rating ?? 1,
      property_info: faqs?.property_info ?? [],
      similar_questions: (faqs?.similar_questions ?? []).map((item) => ({ question: item, key: randomId() })),
      recommended_questions: (faqs?.recommended_questions ?? []).map((item) => ({ question: item, key: randomId() })),
    };

    if (faqId) {
      const data: FAQDetail = await detailFAQ({
        faqId: faqId,
      });

      Object.assign(values, {
        question: data.question ?? '',
        answer: data.answer ?? '',
        type: data.type,
        similar_questions: data.similar_questions?.map((item) => ({ question: item, key: randomId() })),
        recommended_questions: data.recommended_questions?.map((item) => ({ question: item, key: randomId() })),
        // sources: data.sources ?? '',
      });
      // if (data.citation) {
      //   setSourceCitation(data.citation);
      // }
      if (data.type) {
        setAnswerType(data.type);
        setAnswerTypeLoading(false);
        if (data.type === 'source' && data.resources && data.resources.length > 0) {
          data.resources.map((item) => {
            const pageNumbers = item.page_numbers ?? [];
            setSelectedFile(item.vector_file);
            form.setFieldValue('selectedFile', item.vector_file);
            form.setFieldValue('page_numbers', pageNumbers.join(','));
            setFilePageCount(pageNumbers.join(','));
          });
        }
      }
      values.datasetId = data.dataset_id;
      values.is_search = data.is_search;
    }
    form.setValues(values);
    if (!values.recommended_questions.length) addCustomFormQuestions('recommended_questions');

    setInited(false);
  };

  useEffect(() => {
    formInit();
  }, []);

  const getFilesList = async () => {
    setSearchFileLoading(true);
    await getDatasetsFilesList({
      id: DatasetId,
      file_type: 'document',
      query: debouncedSearch.normalize('NFD'),
    }).then((res) => {
      const { items } = res.data;
      setFilesList(items);
      setSearchFileLoading(false);
    });
  };

  useEffect(() => {
    if (debouncedSearch) {
      getFilesList();
    } else {
      setFilesList([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (faqId) return;
    if (selectedFile) {
      form.setFieldValue('selectedFile', undefined);
      setSelectedFile(null);
    }
    if (sourceSearch) {
      getFilesList();
    }
  }, [form.values.datasetId]);

  useEffect(() => {
    form.setFieldValue('sourceSearch', '');
    setSourceSearch('');
    setFilesList([]);
  }, [selectedFile]);

  const chooseAnswerType = (type: string) => {
    setAnswerType(type);
    form.setFieldValue('type', type as 'answer' | 'source');
    // form.setFieldError('sourceSearch', '');
    // form.setFieldError('selectedFile', '');
    // setSourceSearch('');
    // setFilesList([]);
    // setSelectedFile(null);
  };

  const similarQuestionsFields = form.values.similar_questions?.map((item, index) => (
    <Group key={item.key} mt={3}>
      <TextInput
        placeholder={t('faqs.form.input_similar_questions')}
        withAsterisk
        style={{ flex: 1 }}
        {...form.getInputProps(`similar_questions.${index}.question`)}
        rightSection={
          <ActionIcon
            color="gray.5"
            variant="subtle"
            radius={5}
            onClick={() => form.removeListItem('similar_questions', index)}
          >
            <IconX size="1rem" />
          </ActionIcon>
        }
      />
    </Group>
  ));

  const recommendQuestionsFields = form.values.recommended_questions?.map((item, index) => (
    <Group key={item.key} mt="xs">
      <TextInput
        placeholder={t('faqs.form.recommended_questions')}
        withAsterisk
        style={{ flex: 1 }}
        {...form.getInputProps(`recommended_questions.${index}.question`)}
        rightSection={
          <ActionIcon
            color="gray.5"
            variant="subtle"
            radius={5}
            onClick={() => form.removeListItem('recommended_questions', index)}
          >
            <IconX size="1rem" />
          </ActionIcon>
        }
      />
    </Group>
  ));

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {selectDatasetId && <FaqsDatasetForm disabled={!!faqId} {...form.getInputProps('datasetId')} />}

        <TextInput
          ref={focusTrapRef}
          withAsterisk
          label={t('faqs.form.question')}
          labelProps={{ fw: 400, mb: 5 }}
          placeholder={t('faqs.form.question_placeholder')}
          {...form.getInputProps('question')}
          data-autofocus
        />

        <Stack gap={5}>
          <Group justify="space-between" gap={'xs'}>
            <label className="text-sm">{t('faqs.form.similar_questions')}</label>
            <Button size="compact-sm" variant="subtle" onClick={() => addCustomFormQuestions('similar_questions')}>
              <Group gap={5}>
                <IconPlus size={14} />
                {t('embeddings.dataset.add_modal.add_btn')}
              </Group>
            </Button>
          </Group>

          {!form.values.similar_questions?.length && (
            <Button
              className={`${!HasQuestionValue && 'cursor-not-allowed'}`}
              size="lg"
              variant="light"
              fullWidth
              onClick={() => {
                if (HasQuestionValue) generateSQ();
              }}
            >
              {generateLoading ? (
                <Loader size={'sm'} />
              ) : (
                <Text size={'sm'} c={!HasQuestionValue ? 'violet.3' : ''}>
                  {t('faqs.form.generate_similar_questions')}
                </Text>
              )}
            </Button>
          )}

          {similarQuestionsFields}
        </Stack>

        {answerTypeLoading ? (
          <Stack gap={5}>
            <Skeleton height={20} width={100} />
            <Skeleton height={70} mt={5} />
            <Skeleton height={70} mt={5} />
          </Stack>
        ) : (
          <section>
            <label className="text-sm">
              {t('faqs.answer_type.title')}
              <span className="text-red-600" aria-hidden="true">
                {' *'}
              </span>
            </label>

            <Stack mt={10}>
              <Accordion
                variant="separated"
                value={answerType}
                chevron={null}
                onChange={(value) => {
                  if (!value) return;
                  chooseAnswerType(value);
                }}
              >
                <Accordion.Item value="answer" className={classes.accordionItem}>
                  <Accordion.Control>
                    <Radio checked={answerType === 'answer'} label={t('faqs.answer_type.answer_title')} readOnly />
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" c="gray.4">
                      {t('faqs.answer_type.answer_desc')}
                    </Text>

                    <Box mt={12}>
                      {inited ? (
                        <Center mih={150}>
                          <Loader size={'xs'} />
                        </Center>
                      ) : (
                        <>
                          <ErrorBoundary fallback={() => <ErrorContent type="editor" />}>
                            <RichEditor
                              datasetId={DatasetId}
                              placeholder={t('faqs.form.answer_placeholder')}
                              defaultValue={form.values.answer ?? ''}
                              onChange={richEditorOnChange}
                              onUploadState={(uploading: boolean) => setDisabled(uploading)}
                            />
                          </ErrorBoundary>
                          {form.getInputProps('answer').error && (
                            <div className="text-red-600 text-xs mt-1">{form.getInputProps('answer').error}</div>
                          )}
                        </>
                      )}
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
                {!isLocalMode && (
                  <Accordion.Item value="source" className={classes.accordionItem}>
                    <Accordion.Control>
                      <Radio checked={answerType === 'source'} label={t('faqs.answer_type.source_title')} readOnly />
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text size="sm" c="gray.4">
                        {t('faqs.answer_type.source_desc')}
                      </Text>
                      <Box mt={12}>
                        <Stack gap={10}>
                          {!selectedFile ? (
                            <Stack gap={10} className="relative">
                              <TextInput
                                placeholder={t('faqs.answer_type.search_placeholder')}
                                leftSection={<IconSearch size={14} />}
                                value={sourceSearch}
                                {...form.getInputProps('sourceSearch')}
                                onChange={(e) => {
                                  setSourceSearch(e.target.value);
                                  form.setFieldValue('sourceSearch', e.target.value);
                                }}
                              />
                              <Collapse
                                in={
                                  !searchFileLoading &&
                                  (filesList.length > 0 || (filesList.length === 0 && !!debouncedSearch))
                                }
                                className="absolute top-full w-full bg-white rounded-lg shadow-md mt-2 z-20 border"
                              >
                                <Stack className="p-2" gap={0}>
                                  {filesList.length === 0 && !!debouncedSearch && (
                                    <Text className="text-center">{t('faqs.no_file_tip')}</Text>
                                  )}
                                  {filesList.length > 0 &&
                                    filesList.map((item, index) => {
                                      return (
                                        <Group
                                          key={index}
                                          gap={2}
                                          className={`cursor-pointer hover:bg-violet-100 pl-2 rounded-md text-sm w-full`}
                                          justify="space-between"
                                          wrap="nowrap"
                                        >
                                          <Group gap={4} className="text-sm grow overflow-hidden" wrap="nowrap">
                                            {item.file_type === 'html' ? (
                                              <IconLink className="flex-none" size={16} />
                                            ) : (
                                              <IconFileDescription className="flex-none" size={16} />
                                            )}
                                            <Tooltip label={item.filename} withArrow multiline>
                                              <Text size="sm" lineClamp={1} className="break-words">
                                                {item.filename}
                                              </Text>
                                            </Tooltip>
                                          </Group>
                                          <Group gap={0} wrap="nowrap" className="flex-none">
                                            {item.file_type === 'html' ? (
                                              <a href={safeDecodeURI(item.filename)} target="_blank" rel="noreferrer">
                                                <Button size="sm" className="px-2" variant="transparent">
                                                  {t('faqs.answer_type.get_detail')}
                                                </Button>
                                              </a>
                                            ) : (
                                              <Button
                                                size="sm"
                                                className="px-2"
                                                variant="transparent"
                                                onClick={() => {
                                                  // 复用项目已有的文件预览基础设施（PDFViewer + useFilePreviewStore）
                                                  // 设置 file_id 即可触发预览弹窗，并重置上一次预览残留的状态
                                                  useFilePreviewStore.setState({
                                                    file_id: item.id,
                                                    title: item.filename,
                                                    page: 0,
                                                    content: '',
                                                    rows: [],
                                                    highlightPositions: [],
                                                  });
                                                }}
                                              >
                                                {t('faqs.answer_type.get_detail')}
                                              </Button>
                                            )}
                                            <span className="text-gray-400">|</span>
                                            <Button
                                              size="sm"
                                              className="px-2"
                                              variant="transparent"
                                              onClick={() => {
                                                setSelectedFile(item);
                                                form.setFieldValue('selectedFile', item);
                                              }}
                                            >
                                              {t('faqs.answer_type.select')}
                                            </Button>
                                          </Group>
                                        </Group>
                                      );
                                    })}
                                </Stack>
                              </Collapse>
                            </Stack>
                          ) : (
                            <Stack gap={10}>
                              <label className="text-sm">
                                <span
                                  className="hover:text-violet-600 transition-all cursor-pointer underline"
                                  onClick={() => {
                                    setSelectedFile(null);
                                    form.setFieldValue('selectedFile', undefined);
                                    form.setFieldValue('page_numbers', undefined);
                                  }}
                                >
                                  {t('faqs.answer_type.search')}
                                </span>
                                {' > '}
                                <span>{t('faqs.answer_type.knowledge')}</span>
                              </label>
                              <Stack gap={10}>
                                <TextInput
                                  leftSection={
                                    selectedFile.file_type === 'html' ? (
                                      <IconLink size={14} />
                                    ) : (
                                      <IconFileDescription size={14} />
                                    )
                                  }
                                  value={selectedFile.filename}
                                  disabled
                                />
                                {selectedFile.file_type === 'html' ? (
                                  <TextInput value={selectedFile.title || ''} disabled />
                                ) : (
                                  <TextInput
                                    placeholder={t('faqs.answer_type.page_placeholder')}
                                    value={filePageCount}
                                    {...form.getInputProps('page_numbers')}
                                    onChange={(e) => {
                                      let value = e.target.value;
                                      value = value.replace(/[^0-9,]/g, '');
                                      const formattedInput = value
                                        .split(',')
                                        .filter((_, index) => index < 5) //限制只能有五组数据
                                        .map((item) => item.replace(/[^\d]/g, '')) //移除除数字外的任何字符
                                        .join(',');
                                      setFilePageCount(formattedInput);
                                      form.setFieldValue('page_numbers', formattedInput);
                                    }}
                                  />
                                )}
                              </Stack>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}
              </Accordion>
            </Stack>

            {form.getInputProps('type').error && (
              <div className="text-red-600 text-xs mt-3">{form.getInputProps('type').error}</div>
            )}
          </section>
        )}

        <section>
          <Group justify="space-between" gap={'xs'}>
            <label className="text-sm">{t('faqs.form.recommended_questions')}</label>

            <Button size="compact-sm" variant="subtle" onClick={() => addCustomFormQuestions('recommended_questions')}>
              <Group gap={5}>
                <IconPlus size={14} />
                {t('embeddings.dataset.add_modal.add_btn')}
              </Group>
            </Button>
          </Group>

          {recommendQuestionsFields}
        </section>

        {/* 编辑既有 Q&A 时才有「其它语言」可言；关闭后本次修改不会覆盖同一语言包的其它语言 */}
        {faqId && (
          <section>
            <Group gap={4} wrap="nowrap">
              <Switch
                size="sm"
                checked={!!form.values.sync_translations}
                onChange={(event) => form.setFieldValue('sync_translations', event.currentTarget.checked)}
                label={t('faqs.form.sync_translations')}
              />
              {/* 图标放在 Switch 外面：Mantine 的 label 整块可点击，塞进去会连带拨动开关 */}
              <Tooltip label={t('faqs.form.sync_translations_tip')} multiline w={260} position="top" withArrow>
                <ActionIcon size="sm" variant="subtle" color="gray">
                  <IconInfoCircle size="0.875rem" />
                </ActionIcon>
              </Tooltip>
            </Group>
          </section>
        )}

        {FooterComponent ? (
          <FooterComponent loading={loading} disabled={disabled} />
        ) : (
          <section className="flex justify-end mt-5">
            <Group>
              <Button variant="outline" onClick={() => closeAllModals()}>
                {t('alert_cancel')}
              </Button>
              <Button type="submit" loading={loading} disabled={disabled}>
                {onSubmitText ? onSubmitText : t('save')}
              </Button>
            </Group>
          </section>
        )}
      </Stack>

      {/* 来源型答案的文件预览弹窗，由 useFilePreviewStore 中的 file_id 控制显隐 */}
      <PDFViewer />
    </form>
  );
};
